/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import * as authApi from "../api/auth";
import * as billingApi from "../api/billing";
import { BillingInfo } from "../types/billing"; // Import BillingInfo type
import { usePostHog } from "posthog-js/react"; // <<< ADDED IMPORT
import { logger } from "../utils/logger";
import { NotFoundError } from "../api/errors";
import { signupReferralProps } from "../utils/referral";

interface User {
  user_id: string;
  email: string;
  account_tier_id: number;
  created_at: string;
  updated_at: string;
  email_verified: boolean;
  beta_acknowledged: boolean; // Added for beta agreement status
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean; // To handle initial auth check
  login: (credentials: authApi.LoginRequest) => Promise<void>;
  loginWithToken: (token: string, method: string) => Promise<void>;
  register: (credentials: authApi.RegisterRequest) => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<void>; // Function to check on load
  // NEW Billing State
  billingInfo: BillingInfo | null;
  isBillingLoading: boolean;
  billingError: string | null;
  fetchBillingInfo: () => Promise<void>; // Function to manually refetch billing info
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("authToken")
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    !!localStorage.getItem("authToken")
  );
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start as loading
  const posthog = usePostHog(); // <<< GET POSTHOG INSTANCE

  // NEW Billing State
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [isBillingLoading, setIsBillingLoading] = useState<boolean>(false); // Don't start loading initially
  const [billingError, setBillingError] = useState<string | null>(null);

  // Function to fetch billing info
  const fetchBillingInfo = useCallback(async () => {
    logger.debug(
      "[AuthContext] Fetching and verifying billing info with Stripe"
    );
    const currentToken = localStorage.getItem("authToken");
    if (!currentToken) {
      logger.debug("[AuthContext] No token found, skipping billing info fetch");
      setBillingInfo(null);
      return;
    }

    setIsBillingLoading(true);
    setBillingError(null);
    try {
      // Calls the new function that hits the backend endpoint for Stripe verification
      const info = await billingApi.getVerifiedBillingStatus();
      setBillingInfo(info);
      logger.debug("[AuthContext] Verified billing info fetched", {
        subscriptionPlan: info?.subscription_plan,
      });
    } catch (error: unknown) {
      // 404 means no billing record exists (user is on free plan) - not an error
      if (error instanceof NotFoundError) {
        logger.debug(
          "[AuthContext] No billing record found (user is on free plan)",
          {
            errorName: error.name,
          }
        );
        setBillingInfo(null);
        setBillingError(null); // Don't set error for missing billing records
      } else {
        // Only log and set error for actual errors (not 404s)
        logger.error(
          "[AuthContext] Error fetching verified billing info",
          error as Error
        );
        const errorMessage =
          error instanceof Error
            ? error.message
            : typeof error === "object" &&
                error !== null &&
                "message" in error &&
                typeof error.message === "string"
              ? error.message
              : "Failed to fetch and verify billing details.";
        setBillingError(errorMessage);
        setBillingInfo(null);
      }
    } finally {
      setIsBillingLoading(false);
    }
  }, []);

  // Function to check auth status, potentially using /api/me
  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);
    const storedToken = localStorage.getItem("authToken");
    if (storedToken) {
      try {
        // Verify token by fetching user profile
        const currentUser = await authApi.getMe(); // Assumes getMe uses token from localStorage via getAuthToken helper
        setUser(currentUser);
        setToken(storedToken);
        setIsAuthenticated(true);
        // Fetch billing info AFTER successful auth check
        await fetchBillingInfo();
      } catch (error) {
        logger.error(
          "Auth check failed",
          error instanceof Error ? error : new Error(String(error))
        );
        // Token might be invalid or expired
        localStorage.removeItem("authToken");
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
        setBillingInfo(null); // Clear billing info on auth fail
      }
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      setBillingInfo(null); // Clear billing info if no token
    }
    setIsLoading(false);
    // Include fetchBillingInfo in dependency array
  }, [fetchBillingInfo]);

  // Check auth status on initial load
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]); // Depend on the memoized checkAuthStatus

  const login = async (credentials: authApi.LoginRequest) => {
    const response = await authApi.loginUser(credentials);
    await loginWithToken(response.token, "email");
  };

  // Starts a session from a token the server issued, e.g. for a password
  // login or an email's one-click sign-in link.
  const loginWithToken = async (newToken: string, method: string) => {
    localStorage.setItem("authToken", newToken);
    setToken(newToken);
    // Fetch user details after login to populate user state
    const currentUser = await authApi.getMe();
    setUser(currentUser);
    setIsAuthenticated(true);

    // PostHog identify and event
    if (currentUser && posthog) {
      posthog.identify(currentUser.user_id, {
        email: currentUser.email,
        // Add any other user properties you want to track
      });
      posthog.capture("user_logged_in", {
        method,
        timestamp: new Date().toISOString(),
      });
    }

    // Fetch billing info after successful login
    await fetchBillingInfo();
  };

  const register = async (credentials: authApi.RegisterRequest) => {
    const response = await authApi.registerUser(credentials);
    localStorage.setItem("authToken", response.token);
    setToken(response.token);
    // Fetch user details after registration
    const currentUser = await authApi.getMe();
    setUser(currentUser);
    setIsAuthenticated(true);

    // PostHog identify and event
    if (currentUser && posthog) {
      posthog.identify(currentUser.user_id, {
        email: currentUser.email,
        // Add any other user properties you want to track
      });
      posthog.capture("user_signed_up", {
        method: "email", // Assuming email/password registration
        timestamp: new Date().toISOString(),
        ...signupReferralProps(),
      });
    }

    // Fetch billing info after successful registration
    await fetchBillingInfo();
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setBillingInfo(null); // Clear billing info on logout

    // PostHog event and reset
    if (posthog) {
      posthog.capture("user_logged_out", {
        timestamp: new Date().toISOString(),
      });
      posthog.reset(); // Clears PostHog's anonymous ID and any identified user data
    }
    // Optionally redirect to home or login page via navigate hook if needed outside component
  };

  // Use useMemo to prevent unnecessary re-renders of context consumers
  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      token,
      isLoading,
      login,
      loginWithToken,
      register,
      logout,
      checkAuthStatus,
      // Add billing state to context value
      billingInfo,
      isBillingLoading,
      billingError,
      fetchBillingInfo,
      // Add billing state and fetcher to dependency array
    }),
    // login, loginWithToken, logout, and register are stable functions that don't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAuthenticated,
      user,
      token,
      isLoading,
      checkAuthStatus,
      billingInfo,
      isBillingLoading,
      billingError,
      fetchBillingInfo,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
